/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller } from 'react-hook-form';
import {
  Grid, Skeleton,
} from '@mui/material';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  Inputs,
  InputSkeleton,
  CountrySelector,
  getFieldProps,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { LoadingButton } from '@mui/lab';
import { ViewProps } from './types';
import { validateEmail } from '../../../../../utils/helpers/validation';
import MultiEmailInput from '../../../../../components/multi-email-input/multi-email-input';

const validateEmails = (emails: string[]) => {
  const invalidEmails = emails.filter((email) => !validateEmail(email));
  if (invalidEmails.length > 0) {
    return `Invalid email(s): ${invalidEmails.join(', ')}`;
  }
  return true;
};

const EditBillingInfoFormView = ({
  control,
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  open,
  onCancel,
  submitHandler,
  register,
  submissionError,
  partner,
}: ViewProps) => (
  <FormDialog
    open={open}
  >
    <FormDialogTitle handleClose={onCancel}>
      {partner?.billingAddress
        ? 'Edit Billing Info'
        : <Skeleton variant="rounded" />}
    </FormDialogTitle>
    <FormElement name="billing-info-editor-form" onSubmit={handleSubmit(submitHandler)}>
      <FormDialogContent>
        {!partner ? (
          <FormInputsStack>
            <InputSkeleton key="name" />
            <InputSkeleton key="email" />
            <InputSkeleton key="address" />
            <InputSkeleton key="city" />
            <InputSkeleton key="state" />
            <InputSkeleton key="postal-code" />
            <InputSkeleton key="country" />
          </FormInputsStack>
        ) : (
          <FormInputsStack>
            {submissionError && <FormSubmissionError {...submissionError} />}
            <Inputs.Text
              defaultValue={partner?.billingAddress?.name}
              error={!!errors?.name}
              helperText={errors?.name?.message as string}
              id="name"
              label="Company Name"
              required
              {...register('name', { required: 'Please enter a company name' })}
            />
            <Controller
              name="emails"
              control={control}
              rules={{ validate: validateEmails, required: 'Please add at least one email' }}
              render={({ field, fieldState }) => (
                <MultiEmailInput
                  value={field.value || []}
                  onChange={(event, newValue) => {
                    field.onChange(newValue);
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : 'Invoices and billing notifications will be sent to this email(s).'}
                  name="emails"
                />
              )}
            />

            <Inputs.Text
              defaultValue={partner?.billingAddress?.street}
              error={!!errors?.address}
              helperText={errors?.address?.message as string}
              id="address"
              label="Address"
              {...register('address')}
            />
            <Inputs.Text
              defaultValue={partner?.billingAddress?.city}
              error={!!errors?.city}
              helperText={errors?.city?.message as string}
              id="city"
              label="City"
              {...register('city')}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Inputs.Text
                  defaultValue={partner?.billingAddress?.state}
                  error={!!errors?.state}
                  helperText={errors?.state?.message as string}
                  id="state"
                  label="State / Province"
                  {...register('state')}
                />
              </Grid>
              <Grid item xs={6}>
                <Inputs.Text
                  defaultValue={partner?.billingAddress?.postalCode}
                  error={!!errors?.postalCode}
                  helperText={errors?.postalCode?.message as string}
                  id="postalCode"
                  label="Postal / Zip Code"
                  {...register('postalCode')}
                />
              </Grid>
            </Grid>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  {...field}
                  {...getFieldProps('country', errors)}
                  label="Country"
                  onChange={(_: any, newValue: any) => {
                    field.onChange(newValue?.value || null);
                  }}
                />
              )}
            />
          </FormInputsStack>
        )}
      </FormDialogContent>
      <FormDialogActions>
        <FormCancelButton onClick={onCancel} />
        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Submit
        </LoadingButton>
      </FormDialogActions>
    </FormElement>
  </FormDialog>
);
export default EditBillingInfoFormView;
